import { z } from "zod";
import { Id } from "./ids.js";

export const MEMORY_ZONE_NAME_MAX_LENGTH = 60;
export const MemoryZoneNameSchema = z.string().trim().min(1).max(MEMORY_ZONE_NAME_MAX_LENGTH);

/**
 * What a zone holds: the company's facts (one per Space), a department's memory, which its bots
 * share, or a project's, which chosen bots read.
 */
export const MEMORY_ZONE_KINDS = ["company", "department", "project"] as const;
export const MemoryZoneKindSchema = z.enum(MEMORY_ZONE_KINDS);
export type MemoryZoneKind = z.infer<typeof MemoryZoneKindSchema>;
/** The kinds an admin can pick; the Company zone is made by setup. */
export const EditableMemoryZoneKindSchema = z.enum(["department", "project"]);

/** Bots in a Space that share memory with each other and nobody else. */
export const MemoryZoneSchema = z.object({
  id: Id,
  name: z.string(),
  /** The Space's Company zone: every bot reads it, and it cannot be removed. */
  company: z.boolean().optional(),
  kind: MemoryZoneKindSchema.default("department"),
  botIds: z.array(Id),
  /** A project zone's readers: the bots it is shared with. */
  sharedBotIds: z.array(Id).default([]),
});
export type MemoryZone = z.infer<typeof MemoryZoneSchema>;

/**
 * A Space's zones and the bots that can be placed in them: every bot in the Space for an admin,
 * the bots they can see for anyone else. `canManage` is whether the caller may change zones.
 */
export const MemoryZonesViewSchema = z.object({
  canManage: z.boolean(),
  zones: z.array(MemoryZoneSchema),
  bots: z.array(z.object({ id: Id, name: z.string(), memoryZoneId: Id.nullable() })),
});
export type MemoryZonesView = z.infer<typeof MemoryZonesViewSchema>;

/** Memory a bot may read but never write: other zones and other bots' own memory. */
export const BotMemoryGrantsSchema = z.object({
  botId: Id,
  zoneIds: z.array(Id),
  botIds: z.array(Id),
});
export type BotMemoryGrants = z.infer<typeof BotMemoryGrantsSchema>;

export const MEMORY_GRANTS_MAX = 50;
